import React, { useMemo } from "react";
import { X, AlertTriangle, Archive, Trash2 } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../../lib/utils";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "../../components/ui/dialog";

export function DeleteConfirmationModal({
  open,
  onOpenChange,
  row,
  masterType,
  onConfirmDeactivate,
  onConfirmDelete,
}) {
  // Resolve Master Type label (API category name, or legacy keys)
  const masterTypeLabel = useMemo(() => {
    if (masterType === "uom") return "Unit of Measure";
    if (masterType === "tax") return "Tax & Duties";
    if (masterType === "currency") return "Currencies";
    return masterType || "";
  }, [masterType]);

  const isBaseCurrency =
    (masterType === "currency" || masterType === "Currency") &&
    row?.base === "Yes";
  const usageCount = row?.usageCount || 0;

  // Determine Modal State: 'base-currency' | 'no-usage' | 'in-use'
  const modalState = useMemo(() => {
    if (isBaseCurrency) return "base-currency";
    if (usageCount === 0) return "no-usage";
    return "in-use";
  }, [isBaseCurrency, usageCount]);

  const title = useMemo(() => {
    if (!row) return "";
    if (modalState === "base-currency") return "Action Not Allowed";
    if (modalState === "no-usage") return `Deactivate or delete "${row.name}"?`;
    return `Deactivate "${row.name}"?`;
  }, [modalState, row]);

  const subtitle = useMemo(() => {
    if (!row) return "";
    if (modalState === "base-currency") {
      return "The base currency cannot be deactivated or deleted.";
    }
    if (modalState === "no-usage") {
      return "Nothing references this value, so you can deactivate it — keeping it available to reactivate later — or delete it permanently.";
    }
    return `This value is currently used by ${usageCount} records, so it cannot be deleted. You can deactivate it instead to hide it from new drop-downs without affecting historical records.`;
  }, [modalState, usageCount, row]);

  if (!row) return null;

  const handleDeactivate = () => {
    onConfirmDeactivate(row);
    onOpenChange(false);
  };

  const handleDelete = () => {
    onConfirmDelete(row);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        {/* Centered Modal Backdrop overlay */}
        <DialogOverlay className="bg-[#0a1230]/40 backdrop-blur-sm fixed inset-0 z-40 transition-opacity animate-in fade-in duration-200" />

        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "w-[95vw] sm:w-[90vw] md:w-[540px] max-w-[540px]",
            "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60",
            "p-6 flex flex-col gap-5 font-sans focus:outline-none",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          )}
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-4 shrink-0">
            <div>
              <DialogTitle className="text-[20px] font-bold text-foreground tracking-tight leading-snug">
                {title}
              </DialogTitle>
              <p className="text-xs text-[var(--color-text-secondary)] mt-2 font-medium leading-relaxed">
                {subtitle}
              </p>
            </div>
            <DialogPrimitive.Close className="h-9 w-9 shrink-0 grid place-items-center rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-workspace)]/60 hover:text-[var(--color-primary)] transition focus:outline-none">
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </div>

          {/* Compact Selected Record Summary Card */}
          <div className="rounded-xl border border-[var(--color-text-border)] bg-gray-50/50 p-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs font-semibold">
            <div>
              <span className="text-[10px] text-[var(--color-text-secondary)] block uppercase tracking-wider font-bold">Name</span>
              <span className="text-foreground block mt-1 truncate">{row.name}</span>
            </div>
            <div>
              <span className="text-[10px] text-[var(--color-text-secondary)] block uppercase tracking-wider font-bold">Master Type</span>
              <span className="text-foreground block mt-1 truncate">{masterTypeLabel}</span>
            </div>
            <div>
              <span className="text-[10px] text-[var(--color-text-secondary)] block uppercase tracking-wider font-bold">Status</span>
              <span className="text-foreground block mt-1">{row.status}</span>
            </div>
            <div>
              <span className="text-[10px] text-[var(--color-text-secondary)] block uppercase tracking-wider font-bold">Usage Count</span>
              <span className="text-foreground block mt-1">{usageCount}</span>
            </div>
          </div>

          {/* Conditional Renders based on Usage/Base Currency */}
          {modalState === "base-currency" && (
            <div className="p-4 bg-rose-50 border border-rose-200/50 text-[var(--color-error)] rounded-xl text-xs sm:text-sm font-semibold flex items-start gap-2.5">
              <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold leading-normal text-foreground">Base Currency Restriction</p>
                <p className="font-normal text-xs text-[var(--color-text-secondary)] mt-1.5 leading-relaxed">
                  Assign another currency as the base currency before attempting this action. A configuration must always have exactly one base currency.
                </p>
              </div>
            </div>
          )}

          {modalState === "no-usage" && (
            <div className="rounded-xl border border-[var(--color-text-border)] bg-gray-50/50 p-4 sm:p-5 flex items-start gap-4">
              <div className="text-[32px] sm:text-[40px] font-extrabold text-foreground/80 leading-none shrink-0 tracking-tight select-none">
                0
              </div>
              <div className="text-xs text-[var(--color-text-secondary)] font-medium leading-relaxed pt-0.5">
                transaction rows reference this value, so permanent delete is available.
                <p className="text-[11px] text-[var(--color-error)] font-bold mt-1.5 flex items-center gap-1">
                  ⚠️ Deleting this value is permanent and cannot be undone.
                </p>
              </div>
            </div>
          )}

          {modalState === "in-use" && (
            <div className="rounded-xl border border-[var(--color-text-border)] bg-gray-50/50 p-4 sm:p-5 flex items-start gap-4">
              <div className="text-[32px] sm:text-[40px] font-extrabold text-[var(--color-primary)] leading-none shrink-0 tracking-tight select-none">
                {usageCount}
              </div>
              <div className="text-xs text-[var(--color-text-secondary)] font-medium leading-relaxed pt-0.5">
                transaction rows reference this value, so permanent delete is not available. You can safely deactivate the value instead.
              </div>
            </div>
          )}

          {/* Buttons Footer Panel */}
          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3 pt-2 shrink-0 rounded-b-2xl">
            {modalState === "base-currency" ? (
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="h-11 px-5 rounded-xl border border-[var(--color-text-border)] text-[var(--color-text-secondary)] font-semibold text-sm hover:bg-[var(--color-workspace)]/40 hover:text-[var(--color-primary)] transition shadow-sm cursor-pointer bg-white w-full sm:w-auto"
              >
                Close
              </button>
            ) : modalState === "no-usage" ? (
              <>
                <button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  className="h-11 px-5 rounded-xl border border-[var(--color-text-border)] text-[var(--color-text-secondary)] font-semibold text-sm hover:bg-gray-50 transition shadow-sm cursor-pointer bg-white w-full sm:w-auto"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  className="h-11 px-5 rounded-xl bg-[var(--color-error)] hover:bg-[var(--color-error)]/95 text-white font-semibold text-sm transition shadow-[0_8px_20px_-8px_rgba(239,68,68,0.5)] cursor-pointer w-full sm:w-auto flex items-center justify-center gap-1.5"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Delete permanently</span>
                </button>
                <button
                  type="button"
                  onClick={handleDeactivate}
                  className="h-11 px-5 rounded-xl bg-[var(--color-primary)] hover:bg-[var(--color-accent)] text-white font-semibold text-sm transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer w-full sm:w-auto flex items-center justify-center gap-1.5"
                >
                  <Archive className="h-4 w-4" />
                  <span>Deactivate</span>
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  className="h-11 px-5 rounded-xl border border-[var(--color-text-border)] text-[var(--color-text-secondary)] font-semibold text-sm hover:bg-gray-50 transition shadow-sm cursor-pointer bg-white w-full sm:w-auto"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeactivate}
                  className="h-11 px-5 rounded-xl bg-[var(--color-primary)] hover:bg-[var(--color-accent)] text-white font-semibold text-sm transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer w-full sm:w-auto flex items-center justify-center gap-1.5"
                >
                  <Archive className="h-4 w-4" />
                  <span>Deactivate</span>
                </button>
              </>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
